import { useState, useEffect } from 'react';
import { getTimesheets, createTimesheet, updateTimesheet, deleteTimesheet } from '../firebase/db';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { generateDemoTimesheets } from '../demo/demoData';

export const useTimesheets = (jobId = null, fetchAll = false) => {
  const { user, profile, isAdmin } = useAuth();
  const { demoMode } = useDemo();
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTimesheets = async () => {
    setLoading(true);
    if (demoMode) {
      setTimesheets(generateDemoTimesheets(jobId, user?.uid));
      setLoading(false);
      return;
    }
    const shouldFetchAll = fetchAll || isAdmin;
    const data = jobId ? await getTimesheets(jobId) : await getTimesheets(null, user?.uid, shouldFetchAll);
    setTimesheets(data);
    setLoading(false);
  };

  const addTimesheet = async (data) => {
    if (demoMode) {
      const ts = {
        id: 'demo_ts_' + Date.now(),
        ...data,
        workerId: user?.uid,
        workerName: profile?.displayName || 'Demo Worker',
        createdAt: new Date().toISOString(),
      };
      setTimesheets((prev) => [ts, ...prev]);
      return { id: ts.id, error: null };
    }
    return await createTimesheet({ ...data, workerId: user?.uid, workerName: profile?.displayName || '' });
  };

  const updateTs = async (id, data) => {
    if (demoMode) {
      setTimesheets((prev) => prev.map((t) => (t.id === id ? { ...t, ...data } : t)));
      return { error: null };
    }
    return await updateTimesheet(id, data);
  };

  useEffect(() => {
    if (user || demoMode) fetchTimesheets();
  }, [user, demoMode, jobId]);

  const removeTimesheet = async (id) => {
    if (demoMode) {
      setTimesheets((prev) => prev.filter((t) => t.id !== id));
      return { error: null };
    }
    const res = await deleteTimesheet(id);
    if (!res.error) {
      setTimesheets((prev) => prev.filter((t) => t.id !== id));
    }
    return res;
  };

  return { timesheets, loading, fetchTimesheets, addTimesheet, updateTimesheet: updateTs, removeTimesheet };
};
